import type { LoginResponse, MfaChallenge, Permission, SessionUser } from '@pms/shared';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { post, refreshSession, onSession, setSession } from './api';
import { getDevice } from './device';
import { clearEmergency } from './emergency';
import { queryClient } from './query';

interface AuthState {
  user: SessionUser | null;
  ready: boolean;
  /** Returns the challenge token when the account uses two-step sign-in. */
  login: (username: string, password: string) => Promise<string | null>;
  verifyMfa: (mfaToken: string, code: string) => Promise<void>;
  /** Quick sign-in on this registered group tablet. */
  pinLogin: (userId: string, pin: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (p: Permission) => boolean;
  reload: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const off = onSession((s) => setUser(s?.user ?? null));
    refreshSession().finally(() => setReady(true));
    // Keep the short-lived access token fresh while the app is open.
    const t = setInterval(() => void refreshSession(), 12 * 60_000);
    return () => {
      off();
      clearInterval(t);
    };
  }, []);

  const value: AuthState = {
    user,
    ready,
    async login(username, password) {
      const s = await post<LoginResponse | MfaChallenge>('/auth/login', { username, password });
      if ('mfaRequired' in s) return s.mfaToken;
      setSession(s);
      return null;
    },
    async verifyMfa(mfaToken, code) {
      setSession(await post<LoginResponse>('/auth/mfa/verify', { mfaToken, code }));
    },
    async pinLogin(userId, pin) {
      const device = getDevice();
      if (!device) throw new Error('This tablet is not registered for PIN sign-in');
      setSession(await post<LoginResponse>('/auth/pin', { deviceToken: device.token, userId, pin }));
    },
    async logout() {
      await post('/auth/logout').catch(() => undefined);
      clearEmergency();
      setSession(null);
      queryClient.clear();
    },
    can: (p) => !!user?.permissions.includes(p),
    reload: async () => {
      await refreshSession();
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
